import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { UserProvider } from './user';
import { SettingsProvider } from './settings';

export interface LanguageSpec {
    code: string;
    name: string;
    nativeTranslatedName: string;
}

export interface SelectableLanguage extends LanguageSpec {
    selected: boolean;
}

const DEFAULT_LANGUAGE_CODE = 'en';
const languagesSupported: LanguageSpec[] = [
    {
        code: 'en',
        name: 'English',
        nativeTranslatedName: 'English'
    },
    {
        code: 'ja',
        name: 'Japanese',
        nativeTranslatedName: '日本語'
    }
];

@Injectable()
export class TranslationProvider extends BaseProvider {
    constructor(
        private translate: TranslateService,
        private userProvider: UserProvider,
        private settingsProvider: SettingsProvider
    ) {
        super();

        this.initializeTranslation();
    }

    /**
     * Set the current and default language for the app
     */
    private initializeTranslation() {
        this.translate.setDefaultLang(DEFAULT_LANGUAGE_CODE);

        this.setLanguageFromSystem();
    }

    /**
     * Set the language for the app based on the language specified by
     * the user, browser, system or default
     */
    public setLanguageFromSystem() {
        const languageToUse = this.getSelectedLanguageCode();
        this.setTranslationLanguage(languageToUse);
    }

    /**
     * Return the list of languages available for selection in the app.
     * The currently selected language will be marked as such
     */
    public getSelectableLanguages(): SelectableLanguage[] {
        const selectedLanguageCode = this.getSelectedLanguageCode();

        return languagesSupported.map(spec => {
            return {
                ...spec,
                selected: spec.code === selectedLanguageCode
            };
        });
    }

    /**
     * Return the language selected for use in the app
     */
    private getSelectedLanguageCode() {
        if (this.canLocalize()) {
            const requestedLanguage = this.getUserLanguageCode() || this.getSystemLanguageCode();

            if (this.isLanguageSupported(requestedLanguage)) {
                return requestedLanguage;
            }
        }

        return DEFAULT_LANGUAGE_CODE;
    }

    /**
     * Set the current language for the app
     */
    private setTranslationLanguage(languageCode: string) {
        this.translate.use(languageCode);
    }

    private canLocalize() {
        return this.userProvider.hasExperimentalFeaturesCapability();
    }

    /**
     * Set the user's language preference
     */
    public async setUserLanguageCode(languageCode: string) {
        if (!this.isLanguageSupported(languageCode)) {
            throw new Error(`Language code ${languageCode} is not supported`);
        }

        await this.settingsProvider.set('locale', languageCode);
        this.setLanguageFromSystem();
    }

    private getUserLanguageCode(): string | null {
        return this.settingsProvider.get('locale') || null;
    }

    private getSystemLanguageCode() {
        if (navigator && navigator.language) {
            return navigator.language.split('-')[0];
        }

        return DEFAULT_LANGUAGE_CODE;
    }

    private isLanguageSupported(languageCode: string) {
        return languagesSupported.some(lang => lang.code === languageCode);
    }
}

/**
 * Use this method in your root module to provide the TranslateService
 */
export function getTranslateModuleForRoot() {
    return TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: createHttpLoader,
            deps: [HttpClient]
        }
    });
}

export function createHttpLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Use this method to mark a string for collection into the translation resource files
 * by the localization register command. This function does nothing at runtime
 *
 * @returns The same value as the key passed in
 */
export function resourceString(key: string) {
    return key;
}



// WEBPACK FOOTER //
// ./src/providers/translation.ts